(() => {
  const LEGACY_WRAPPER = '.product-info-wrapper';

  const icons = {
    description: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6.5 3.5h8l3 3v14H6.5z"></path><path d="M14.5 3.5v4h4"></path><path d="M9 12h6M9 16h5"></path></svg>',
    shipping: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3.5 6.5h10v10h-10z"></path><path d="M13.5 10h3l3 3v3.5h-6z"></path><circle cx="7" cy="18" r="1.8"></circle><circle cx="17" cy="18" r="1.8"></circle></svg>',
    guarantee: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3.5 19 6v5.2c0 4.4-2.8 7.6-7 9.3-4.2-1.7-7-4.9-7-9.3V6z"></path><path d="m8.7 12 2.1 2.1 4.6-4.7"></path></svg>',
    chevron: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m7 9.5 5 5 5-5"></path></svg>'
  };

  const config = {
    description: {
      title: 'Description',
      subtitle: 'Product details & personalization'
    },
    shipping: {
      title: 'Shipping Information',
      subtitle: 'Production, delivery & returns'
    },
    guarantee: {
      title: 'Satisfaction Guarantee',
      subtitle: 'Our promise if something isn’t right'
    }
  };

  const classify = (label) => {
    const text = String(label || '').trim().toLowerCase();
    if (text.includes('description')) return 'description';
    if (text.includes('shipping') || text.includes('return')) return 'shipping';
    if (text.includes('guarantee') || text.includes('satisfaction')) return 'guarantee';
    return '';
  };

  const unwrap = (element) => {
    const parent = element.parentNode;
    if (!parent) return;
    while (element.firstChild) parent.insertBefore(element.firstChild, element);
    element.remove();
  };

  const sanitizeFragment = (source, aggressive) => {
    const fragment = document.createDocumentFragment();
    Array.from(source.childNodes).forEach((node) => fragment.appendChild(node.cloneNode(true)));

    fragment.querySelectorAll('style,script,noscript,meta,link,title,base').forEach((node) => node.remove());
    fragment.querySelectorAll('html,head,body').forEach(unwrap);

    fragment.querySelectorAll('*').forEach((element) => {
      Array.from(element.attributes).forEach((attribute) => {
        const name = attribute.name.toLowerCase();
        if (name.startsWith('on')) element.removeAttribute(attribute.name);
        if (aggressive && ['class', 'id', 'style', 'width', 'height'].includes(name)) {
          element.removeAttribute(attribute.name);
        }
      });

      if (element.tagName === 'A' && element.target === '_blank') {
        element.rel = 'noopener noreferrer';
      }
      if (element.tagName === 'IMG') {
        element.loading = 'lazy';
        element.decoding = 'async';
        element.removeAttribute('width');
        element.removeAttribute('height');
      }
    });

    return fragment;
  };

  const wrapTables = (root) => {
    root.querySelectorAll('table').forEach((table) => {
      if (table.parentElement?.classList.contains('pz-product-table-scroll')) return;
      const wrapper = document.createElement('div');
      wrapper.className = 'pz-product-table-scroll';
      table.parentNode.insertBefore(wrapper, table);
      wrapper.appendChild(table);
    });
  };

  const buildRow = (key, source) => {
    const details = document.createElement('details');
    details.className = `pz-product-detail pz-product-detail--${key}`;
    details.dataset.pzProductDetail = key;

    const summary = document.createElement('summary');

    const icon = document.createElement('span');
    icon.className = 'pz-product-detail__icon';
    icon.innerHTML = icons[key];

    const heading = document.createElement('span');
    heading.className = 'pz-product-detail__heading';

    const title = document.createElement('span');
    title.className = 'pz-product-detail__title';
    title.textContent = config[key].title;

    const subtitle = document.createElement('span');
    subtitle.className = 'pz-product-detail__subtitle';
    subtitle.textContent = config[key].subtitle;

    const chevron = document.createElement('span');
    chevron.className = 'pz-product-detail__chevron';
    chevron.innerHTML = icons.chevron;

    heading.append(title, subtitle);
    summary.append(icon, heading, chevron);

    const panel = document.createElement('div');
    panel.className = 'pz-product-detail__panel';

    const panelInner = document.createElement('div');
    panelInner.className = 'pz-product-detail__panel-inner';

    const richText = document.createElement('div');
    richText.className = 'pz-product-richtext';
    richText.appendChild(sanitizeFragment(source, key !== 'description'));
    wrapTables(richText);

    panelInner.appendChild(richText);
    panel.appendChild(panelInner);
    details.append(summary, panel);
    return details;
  };

  const rebuildWrapper = (legacy) => {
    if (!legacy || legacy.dataset.pzRebuildStarted === 'true') return;
    legacy.dataset.pzRebuildStarted = 'true';

    const sources = {};
    legacy.querySelectorAll('.product-accordion-item').forEach((item) => {
      const label = item.querySelector('.accordion-title')?.textContent || item.querySelector('.product-accordion-header')?.textContent || '';
      const key = classify(label);
      const content = item.querySelector('.accordion-content');
      if (key && content && !sources[key]) sources[key] = content;
    });

    const keys = ['description', 'shipping', 'guarantee'].filter((key) => sources[key]);
    if (!keys.length) return;

    const card = document.createElement('section');
    card.className = 'pz-product-details-card';
    card.setAttribute('aria-label', 'Product information');
    card.dataset.pzProductDetailsCard = 'true';
    keys.forEach((key) => card.appendChild(buildRow(key, sources[key])));

    const share = legacy.querySelector('.social-share-bar');
    if (share) share.classList.add('pz-product-share');

    legacy.parentNode.insertBefore(card, legacy);
    if (share) card.insertAdjacentElement('afterend', share);
    legacy.remove();
  };

  const rebuildAll = (root = document) => {
    root.querySelectorAll?.(LEGACY_WRAPPER).forEach(rebuildWrapper);
  };

  const init = () => {
    rebuildAll();

    const observer = new MutationObserver((records) => {
      records.forEach((record) => {
        record.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) return;
          if (node.matches?.(LEGACY_WRAPPER)) rebuildWrapper(node);
          else rebuildAll(node);
        });
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();

  document.addEventListener('shopify:section:load', (event) => rebuildAll(event.target));
})();
